import { css, html, nothing } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import {
    bucketOf,
    canMoveToGroup,
    distinctBuckets,
    filterRows,
    gridShortcuts,
    groupRows,
    isFilterable,
    isGroupable,
    isSortable,
    menuPlacement,
    neighboursAfterMove,
    nextCell,
    nextOptionStartingWith,
    rangeOfIds,
    sortRows,
    toggleFilterValue,
} from '../../lib/smart-grid.js';

/** The keys that move between cells, and that bring focus into the grid. */
const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

/**
 * <smart-grid>: a spreadsheet-like grid. A page describes its columns
 * (lib/smart-grid.js GridColumn) and hands over its rows; the grid sorts,
 * groups and filters from each column header's menu, edits cells in place,
 * moves between cells from the keyboard, and selects rows. Pressing ? shows
 * its keyboard shortcuts.
 *
 * The grid holds no data of its own. Its view (sort, group, filters, search)
 * is the `state` property, and every change to it is a `state-change` event
 * for the page to keep (in the URL, say). An edited cell is a `cell-commit`
 * event for the page to save; the grid shows whatever rows it's given next.
 *
 * Properties:
 *   columns     GridColumn[]
 *   rows        any[]
 *   rowKey      (row) => string — default row.id
 *   state       GridState
 *   selectable  boolean — a checkbox column, with shift-click ranges
 *   reorderable boolean — rows drag (and Alt+↑/↓) into a manual order, while
 *               no column sort is on; into another group too, when the grouped
 *               column edits to that group's value
 *   selected    Set<string> — the selected row keys
 *   rowActions  (row) => template — a leading cell per row, after the checkbox
 *   actionsLabel string — that column's header
 *   groupExtra  (group, column) => template — more in a group's header row
 *   emptyMessage template|string — shown when there are no rows at all
 *
 * Events (detail):
 *   state-change      the new GridState
 *   cell-commit       { row, key, value } — value is the editor's string
 *   selection-change  the new Set of selected row keys
 *   row-move          { row, rows, prev, next, groupKey } — the row moved and
 *                     every row moving with it (the selection, when the row is
 *                     one of several selected), in order; their new neighbours
 *                     in the group (null at an end); and the group they moved
 *                     into when grouped (null otherwise); the page reorders
 */
class SmartGrid extends BaseComponent {
    static properties = {
        columns: { type: Array, attribute: false },
        rows: { type: Array, attribute: false },
        rowKey: { attribute: false },
        state: { type: Object, attribute: false },
        selectable: { type: Boolean },
        reorderable: { type: Boolean },
        selected: { type: Object, attribute: false },
        rowActions: { attribute: false },
        actionsLabel: { type: String, attribute: 'actions-label' },
        groupExtra: { attribute: false },
        emptyMessage: { attribute: false },
        _menu: { type: String, state: true },
        _focus: { type: Object, state: true },
        _editing: { type: Object, state: true },
        _collapsed: { type: Object, state: true },
        _drop: { type: Object, state: true },
        _refused: { type: Object, state: true },
        _help: { type: Boolean, state: true },
    };

    static styles = css`
        /* A flex column, so the grid fills whatever height its host is given
           and scrolls inside it; with no height given it is as tall as its rows. */
        :host { display: flex; flex-direction: column; min-height: 0; }
        .chips { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 0.5rem; min-height: 0; }
        .chip {
            display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.78rem;
            background: #eef2f7; border: 1px solid #d8dee8; border-radius: 999px; padding: 0.1rem 0.55rem;
        }
        .chip button { border: 0; background: none; padding: 0; line-height: 1; color: #667; }
        .grid-wrap { flex: 1 1 auto; min-height: 0; overflow: auto; border: 1px solid #dee2e6; border-radius: 6px; }
        table { width: 100%; border-collapse: collapse; font-size: 0.85rem; margin: 0; }
        th {
            position: sticky; top: 0; background: #f8f9fa; z-index: 2;
            text-align: left; font-weight: 600; padding: 0; border-bottom: 1px solid #dee2e6;
            white-space: nowrap; user-select: none;
        }
        .th-button {
            display: flex; align-items: center; gap: 0.3rem; width: 100%; border: 0; background: none;
            padding: 0.4rem 0.5rem; font: inherit; text-align: left; color: inherit;
        }
        .th-button:hover, .th-button.open { background: #eceff3; }
        .th-button .indicators { color: #0d6efd; font-size: 0.75rem; display: inline-flex; gap: 0.15rem; }
        .th-button .caret { margin-left: auto; color: #999; font-size: 0.7rem; }
        th.plain { padding: 0.4rem 0.5rem; }
        .menu {
            /* Fixed, placed from the header's rect, so the grid's scroll box can't clip it. */
            position: fixed; z-index: 1050; min-width: 14rem; max-width: 20rem; overflow-y: auto;
            background: white; border: 1px solid #d0d5dc; border-radius: 6px; box-shadow: 0 0.5rem 1.5rem rgba(0,0,0,0.12);
            padding: 0.3rem 0; font-weight: 400; white-space: normal;
        }
        .menu button.item {
            display: flex; align-items: center; gap: 0.5rem; width: 100%; border: 0; background: none;
            padding: 0.3rem 0.75rem; font-size: 0.83rem; text-align: left;
        }
        .menu button.item:hover { background: #f1f4f8; }
        .menu button.item.active { color: #0d6efd; font-weight: 600; }
        .menu hr { margin: 0.3rem 0; }
        .menu .section { padding: 0.2rem 0.75rem; font-size: 0.72rem; color: #888; text-transform: uppercase; letter-spacing: 0.03em; }
        .menu .values { max-height: 14rem; overflow-y: auto; }
        .menu label.value { display: flex; align-items: center; gap: 0.5rem; padding: 0.2rem 0.75rem; font-size: 0.83rem; cursor: pointer; }
        .menu label.value:hover { background: #f1f4f8; }
        .menu .count { margin-left: auto; color: #999; font-size: 0.75rem; }
        td { padding: 0; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
        td.check, th.check { width: 2rem; text-align: center; padding: 0.25rem; }
        .cell {
            display: flex; align-items: center; gap: 0.35rem;
            padding: 0.35rem 0.5rem; min-height: 2rem; outline: none; max-width: 22rem; cursor: default;
        }
        .cell .value { flex: 1 1 auto; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        /* The eliding column takes the room the others leave, down to 16rem; past
           that the grid scrolls sideways rather than squeezing it. Its text never
           widens it (max-width: 0, which min-width overrides) and ends in an ellipsis. */
        th.elide, td.elide { width: 100%; min-width: 16rem; max-width: 0; overflow: hidden; text-overflow: ellipsis; }
        td.elide .cell { max-width: none; }
        .cell.readonly { color: #555; cursor: not-allowed; }
        /* A cell that can't be edited keeps room for its lock, so showing it on
           hover or focus moves nothing; trying to edit it tints it. */
        .cell .lock {
            flex: none; visibility: hidden; color: #9aa0a6; font-size: 0.7rem;
            display: inline-block; width: 0.8rem; height: 0.8rem; line-height: 0.8rem; text-align: center; overflow: hidden;
        }
        .cell.readonly:hover .lock, .cell.readonly:focus .lock, .cell.refused .lock { visibility: visible; }
        .cell.refused { background: #fff4e5; transition: background 0.2s; }
        .cell.editable { cursor: text; }
        .cell.editable:hover:not(:focus) { box-shadow: inset 0 0 0 1px #c9dafc; }
        /* The selected cell looks the same whatever it is: editable, locked or
           refused, and while its list of choices is open. */
        .cell:focus, .cell.editing { box-shadow: inset 0 0 0 2px #0d6efd; background: #f5f9ff; }
        .cell input { flex: 1 1 auto; width: 100%; font-size: 0.85rem; padding: 0.15rem 0.3rem; }
        /* A select cell's choices: a list under the cell, like a header menu. */
        .menu.options { outline: none; }
        .options .option {
            display: flex; align-items: center; gap: 0.5rem; padding: 0.3rem 0.75rem;
            font-size: 0.83rem; cursor: pointer; white-space: nowrap;
        }
        .options .option.highlight { background: #e7f0ff; }
        .options .option.current { font-weight: 600; }
        .options .option .tick { visibility: hidden; color: #0d6efd; }
        .options .option.current .tick { visibility: visible; }
        tr.grid-row:hover td { background: #fafbfc; }
        tr.grid-row.selected td { background: #eef4ff; }
        tr.group-row td {
            background: #f3f4f6; padding: 0.4rem 0.6rem; font-weight: 600;
            border-bottom: 1px solid #e3e5e8; cursor: pointer;
        }
        .group-count { font-weight: 400; color: #666; margin-left: 0.5rem; font-size: 0.8rem; }
        td.drag, th.drag { width: 1.5rem; padding: 0; text-align: center; color: #b0b5bd; }
        td.drag .grip { cursor: grab; display: inline-block; padding: 0.3rem 0.1rem; }
        td.drag .grip.off { cursor: not-allowed; opacity: 0.4; }
        tr.grid-row.dragging td { opacity: 0.4; }
        .drag-badge {
            position: fixed; top: -100px; left: 0; padding: 0.25rem 0.6rem; border-radius: 999px;
            background: #0d6efd; color: white; font-size: 0.8rem; font-weight: 600;
        }
        tr.grid-row.drop-before td { box-shadow: inset 0 2px 0 #0d6efd; }
        tr.grid-row.drop-after td { box-shadow: inset 0 -2px 0 #0d6efd; }
        tr.group-row.drop-into td { box-shadow: inset 0 0 0 2px #0d6efd; }
        /* A leading cell, by the checkbox, so a sideways-scrolled grid can't hide it. */
        td.actions, th.actions { width: 2rem; white-space: nowrap; padding: 0.25rem; text-align: center; }
        .empty { padding: 2rem; text-align: center; color: #888; }
        /* A tight line, with its key sitting in it, so the hint adds no height
           beyond its text. */
        .hint { font-size: 0.85rem; font-weight: 500; color: #555; margin-top: 0.4rem; line-height: 1.2; }
        .hint button { border: 0; background: none; padding: 0; line-height: 1; vertical-align: middle; }
        .hint kbd { padding: 0 0.3rem; line-height: 1.2; vertical-align: top; }
        kbd {
            display: inline-block; padding: 0.05rem 0.35rem; font: inherit; font-size: 0.75rem; line-height: 1.3;
            color: #333; background: #f8f9fa; border: 1px solid #c9ced6; border-bottom-width: 2px; border-radius: 4px;
        }
        .help-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.15); z-index: 1060; }
        .help {
            position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 1061;
            width: min(42rem, calc(100vw - 2rem)); max-height: calc(100vh - 2rem); overflow-y: auto;
            /* Black and see-through, so the grid it describes shows through. */
            background: rgba(0,0,0,0.85); color: #f1f3f5;
            border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; box-shadow: 0 1rem 3rem rgba(0,0,0,0.35); outline: none;
        }
        .help-header { display: flex; justify-content: space-between; align-items: center; padding: 0.9rem 1.1rem; border-bottom: 1px solid rgba(255,255,255,0.15); }
        .help-header h2 { font-size: 1.05rem; font-weight: 600; margin: 0; color: #fff; }
        .help-body { padding: 0.25rem 1.1rem 1rem; display: grid; grid-template-columns: repeat(auto-fit, minmax(17rem, 1fr)); gap: 0 1.5rem; }
        .help h3 { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.03em; color: #adb5bd; margin: 0.9rem 0 0.4rem; }
        .help dl { display: grid; grid-template-columns: auto 1fr; gap: 0.35rem 0.75rem; margin: 0; font-size: 0.83rem; align-items: baseline; }
        .help dt { font-weight: 400; white-space: nowrap; }
        .help dd { margin: 0; }
        .help .or { color: #adb5bd; font-size: 0.72rem; margin: 0 0.2rem; }
        .help .note { font-size: 0.75rem; color: #adb5bd; margin: 0.4rem 0 0; }
        .help kbd { color: #fff; background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.35); }
    `;

    constructor() {
        super();
        this.columns = [];
        this.rows = [];
        this.rowKey = (row) => row.id;
        this.state = { sort: null, group: '', filters: {}, query: '' };
        this.selectable = false;
        this.reorderable = false;
        this._drop = null;
        this._dragKeys = [];
        this._refocus = null;
        this.selected = new Set();
        this.rowActions = null;
        this.actionsLabel = '';
        this.groupExtra = null;
        this.emptyMessage = 'Nothing here yet.';
        this._menu = null;
        this._menuAt = { top: 0, bottom: null, left: 0, maxHeight: 0 };
        this._refused = null;
        this._help = false;
        this._helpReturn = null;
        this._focus = null;
        this._editing = null;
        this._collapsed = new Set();
        this._anchorKey = null;
        this._onDocClick = (e) => {
            if (this._menu && !e.composedPath().some((el) => el.classList?.contains('menu-host'))) {
                this._menu = null;
            }
        };
        // A menu or list of choices is placed where its header or cell was, so
        // it closes when anything but itself scrolls, and when the window resizes.
        this._onScrollOrResize = (e) => {
            if (e.composedPath?.().some((el) => el.classList?.contains('menu'))) return;
            if (this._menu) this._menu = null;
            if (this._editing?.at) this._cancelEdit(false);
        };
        // ? shows the help, and an arrow key starts moving around the grid when
        // nothing in it has focus yet, unless the key is being typed or another
        // dialog has focus.
        this._onDocKeydown = (e) => {
            const arrow = ARROW_KEYS.includes(e.key);
            if (
                (e.key !== '?' && !arrow) ||
                e.defaultPrevented ||
                e.metaKey ||
                e.ctrlKey ||
                e.altKey
            )
                return;
            if (this._help || this._editing || this._menu) return;
            const path = e.composedPath();
            const typing = path.some(
                (el) =>
                    el instanceof HTMLElement &&
                    (el.isContentEditable ||
                        ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) ||
                        el.getAttribute('role') === 'dialog'),
            );
            if (typing) return;
            if (arrow) {
                // Inside the grid, the cells move focus themselves.
                if (path.includes(this)) return;
                const rowCount = this._visibleRows().length;
                if (!rowCount) return;
                e.preventDefault();
                const at = this._focus || { row: 0, col: 0 };
                this._focusCell({
                    row: Math.min(at.row, rowCount - 1),
                    col: Math.min(at.col, this.columns.length - 1),
                });
                return;
            }
            e.preventDefault();
            this._openHelp();
        };
    }

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('click', this._onDocClick);
        document.addEventListener('keydown', this._onDocKeydown);
        window.addEventListener('scroll', this._onScrollOrResize, true);
        window.addEventListener('resize', this._onScrollOrResize);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._onDocClick);
        document.removeEventListener('keydown', this._onDocKeydown);
        window.removeEventListener('scroll', this._onScrollOrResize, true);
        window.removeEventListener('resize', this._onScrollOrResize);
    }

    // --- Derived ---

    get _groups() {
        const filtered = filterRows(this.rows, this.columns, this.state);
        const sorted = sortRows(filtered, this.columns, this.state.sort);
        const unfiltered = !Object.keys(this.state.filters).length && !this.state.query;
        return groupRows(sorted, this.columns, this.state.group, { includeEmpty: unfiltered });
    }

    _visibleRows(groups = this._groups) {
        return groups.flatMap((g) => (this._collapsed.has(g.key) ? [] : g.rows));
    }

    _column(key) {
        return this.columns.find((c) => c.key === key);
    }

    _editable(col, row) {
        return Boolean(col.editor) && (col.editable ? col.editable(row) : true);
    }

    // --- Events out ---

    _setState(next) {
        this.dispatchEvent(new CustomEvent('state-change', { detail: { ...this.state, ...next } }));
    }

    _setSelected(next) {
        this.dispatchEvent(new CustomEvent('selection-change', { detail: next }));
    }

    _commit(row, col, value) {
        this.dispatchEvent(
            new CustomEvent('cell-commit', { detail: { row, key: col.key, value } }),
        );
    }

    // --- Header menus ---

    _toggleMenu(e, key) {
        e.stopPropagation();
        this._menu = this._menu === key ? null : key;
        if (this._menu)
            this._menuAt = menuPlacement(e.currentTarget.getBoundingClientRect(), window);
    }

    _sortBy(col, dir) {
        this._setState({ sort: dir ? { key: col.key, dir } : null });
        this._menu = null;
    }

    _groupBy(col) {
        this._setState({ group: this.state.group === col.key ? '' : col.key });
        this._collapsed = new Set();
        this._menu = null;
    }

    _toggleFilter(col, bucketKey) {
        this.dispatchEvent(
            new CustomEvent('state-change', {
                detail: toggleFilterValue(this.state, col.key, bucketKey),
            }),
        );
    }

    _clearFilter(col) {
        const filters = { ...this.state.filters };
        delete filters[col.key];
        this._setState({ filters });
    }

    // --- Cells ---

    _startEdit(row, col, seed) {
        if (!this._editable(col, row)) {
            this._refuse(row, col);
            return;
        }
        this._editing = { key: this.rowKey(row), col: col.key, seed };
        this.updateComplete.then(() => {
            if (col.editor === 'select') {
                this._openChoices(row, col);
                return;
            }
            const el = /** @type {HTMLInputElement} */ (
                this.shadowRoot.querySelector('.cell .editor')
            );
            if (!el) return;
            el.focus();
            if (seed == null) el.select();
            else el.setSelectionRange(el.value.length, el.value.length);
        });
    }

    /**
     * Open a select cell's list of choices under it, the current one
     * highlighted. The cell keeps showing its value, so nothing moves.
     */
    _openChoices(row, col) {
        const cell = this.shadowRoot.querySelector('.cell.editing');
        if (!cell || !this._editing) return;
        const rect = cell.getBoundingClientRect();
        const current = String(col.value(row) ?? '');
        const highlight = this._choicesOf(col).findIndex((o) => o.value === current);
        this._editing = {
            ...this._editing,
            at: menuPlacement(rect, window),
            minWidth: Math.round(rect.width),
            highlight: Math.max(0, highlight),
        };
        this.updateComplete.then(() =>
            /** @type {HTMLElement} */ (this.shadowRoot.querySelector('.options'))?.focus({
                preventScroll: true,
            }),
        );
    }

    _choicesOf(col) {
        return col.editOptions || col.options || [];
    }

    /** Choose a value from a cell's list: saved if it changed, and back to the cell either way. */
    _choose(row, col, value) {
        if (value !== String(col.value(row) ?? '')) this._commit(row, col, value);
        this._cancelEdit(true);
    }

    /** End the edit without saving anything more; refocus the cell, or leave focus where it went. */
    _cancelEdit(refocus) {
        if (!this._editing || this._editing.done) return;
        this._editing = { ...this._editing, done: true };
        this._endEdit(refocus);
    }

    _onChoicesKeydown(e, row, col) {
        const options = this._choicesOf(col);
        const highlight = this._editing.highlight;
        const move = (i) => {
            e.preventDefault();
            this._editing = {
                ...this._editing,
                highlight: Math.max(0, Math.min(options.length - 1, i)),
            };
        };
        switch (e.key) {
            case 'ArrowDown':
                return move(highlight + 1);
            case 'ArrowUp':
                return move(highlight - 1);
            case 'Home':
                return move(0);
            case 'End':
                return move(options.length - 1);
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (options[highlight]) this._choose(row, col, options[highlight].value);
                return;
            case 'Escape':
            case 'Tab':
                e.preventDefault();
                this._cancelEdit(true);
                return;
        }
        // Typing a letter jumps to the next choice starting with it.
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
            const next = nextOptionStartingWith(options, highlight, e.key);
            if (next !== -1) move(next);
        }
    }

    /** Show that a cell won't edit, rather than doing nothing. */
    _refuse(row, col) {
        const refused = { key: this.rowKey(row), col: col.key };
        this._refused = refused;
        setTimeout(() => {
            if (this._refused === refused) this._refused = null;
        }, 1200);
    }

    _endEdit(refocus) {
        const editing = this._editing;
        this._editing = null;
        if (refocus && editing) {
            const rowIndex = this._visibleRows().findIndex((r) => this.rowKey(r) === editing.key);
            const colIndex = this.columns.findIndex((c) => c.key === editing.col);
            if (rowIndex !== -1) this._focusCell({ row: rowIndex, col: colIndex });
        }
    }

    _focusCell(pos) {
        this._focus = pos;
        this.updateComplete.then(() => {
            /** @type {HTMLElement} */ (
                this.shadowRoot.querySelector(`.cell[data-row="${pos.row}"][data-col="${pos.col}"]`)
            )?.focus();
        });
    }

    _onCellKeydown(e, row, rowIndex, colIndex, rowCount) {
        // Only keys pressed on the cell itself: an editor's Enter bubbles up
        // after it has saved, and must not open the cell for editing again.
        if (this._editing || e.target !== e.currentTarget) return;
        const col = this.columns[colIndex];
        if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown') && this.reorderable) {
            e.preventDefault();
            if (this._moveByKeyboard(row, e.key === 'ArrowUp' ? -1 : 1)) {
                this._refocus = { key: this.rowKey(row), col: colIndex };
            }
            return;
        }
        if (e.key === 'Enter' || e.key === 'F2') {
            e.preventDefault();
            this._startEdit(row, col);
            return;
        }
        if (e.key === ' ' && this.selectable) {
            e.preventDefault();
            this._toggleSelect(this.rowKey(row), false);
            return;
        }
        const next = nextCell(
            { row: rowIndex, col: colIndex },
            e.key,
            rowCount,
            this.columns.length,
            e.shiftKey,
        );
        if (next) {
            e.preventDefault();
            this._focusCell(next);
            return;
        }
        // ? is the help (the document listener opens it), not the start of an edit.
        if (e.key === '?') return;
        // Typing into a text cell starts editing with what was typed.
        if (col.editor === 'text' && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
            e.preventDefault();
            this._startEdit(row, col, e.key);
        }
    }

    _onEditorKeydown(e, row, col) {
        if (e.key === 'Escape') {
            e.preventDefault();
            this._editing = { ...this._editing, done: true };
            this._endEdit(true);
        } else if ((e.key === 'Enter' || e.key === 'Tab') && col.editor === 'text') {
            // Saving is always a deliberate key: Enter stays, Tab moves on.
            e.preventDefault();
            this._commitText(row, col, e.target.value);
            this._editing = { ...this._editing, done: true };
            this._endEdit(true);
            if (e.key === 'Tab') {
                const next = nextCell(
                    this._focus,
                    'Tab',
                    this._visibleRows().length,
                    this.columns.length,
                    e.shiftKey,
                );
                if (next) this._focusCell(next);
            }
        }
    }

    _onEditorBlur() {
        // Clicking away cancels: nothing saves without Enter, Tab or a choice.
        if (!this._editing || this._editing.done) return;
        this._endEdit(false);
    }

    _commitText(row, col, value) {
        const next = value.trim();
        if (next !== String(col.value(row) ?? '')) this._commit(row, col, next);
    }

    // --- Reordering ---

    get _canReorder() {
        return this.reorderable && !this.state.sort;
    }

    /** The group a row is shown in, as the grid groups it. */
    _groupOf(row) {
        const groups = this._groups;
        return groups.find((g) => g.rows.includes(row)) || groups[0];
    }

    _renderGrip(row) {
        if (!this._canReorder) {
            return html`<td class="drag"><span class="grip off" title="Clear the sort to reorder by dragging"><i class="bi bi-grip-vertical"></i></span></td>`;
        }
        if (!this._grabbable(row)) return html`<td class="drag"></td>`;
        const many = this._movingRows(row).length > 1;
        return html`<td class="drag">
            <span class="grip" draggable="true" title="${many ? 'Drag to move the selected rows' : 'Drag to reorder'}"
                @dragstart="${(e) => this._onDragStart(e, row)}"
                @dragend="${() => {
                    this._dragKeys = [];
                    this._drop = null;
                }}"><i class="bi bi-grip-vertical"></i></span>
        </td>`;
    }

    /**
     * Whether a row can be picked up: any row with nothing selected, and only
     * the selected rows once something is, so it's clear what moves.
     */
    _grabbable(row) {
        return !this.selectable || this.selected.size === 0 || this.selected.has(this.rowKey(row));
    }

    /**
     * The rows that move with a row: every selected row shown, in order, when
     * it's one of several selected; otherwise just the row.
     */
    _movingRows(row) {
        const key = this.rowKey(row);
        if (!this.selectable || !this.selected.has(key) || this.selected.size < 2) return [row];
        return this._visibleRows().filter((r) => this.selected.has(this.rowKey(r)));
    }

    _onDragStart(e, row) {
        const moving = this._movingRows(row);
        this._dragKeys = moving.map((r) => this.rowKey(r));
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', this._dragKeys.join('\n'));
        if (moving.length > 1) {
            // A badge for several rows; the browser snapshots it straight away.
            const badge = document.createElement('div');
            badge.className = 'drag-badge';
            badge.textContent = `${moving.length} rows`;
            this.shadowRoot.appendChild(badge);
            e.dataTransfer.setDragImage(badge, 10, 10);
            setTimeout(() => badge.remove());
        } else {
            const tr = /** @type {HTMLElement} */ (e.target).closest('tr');
            if (tr) e.dataTransfer.setDragImage(tr, 10, 10);
        }
    }

    _draggedRows() {
        const keys = new Set(this._dragKeys);
        return this.rows.filter((r) => keys.has(this.rowKey(r)));
    }

    /** Whether every moving row may land in a group. */
    _canLandIn(rows, group) {
        const groupCol = this.state.group && this._column(this.state.group);
        return !groupCol || rows.every((row) => canMoveToGroup(groupCol, row, group.key));
    }

    _onDragOver(e, group, targetRow) {
        const moving = this._draggedRows();
        if (!moving.length || !group || !this._canLandIn(moving, group)) return;
        if (targetRow && this._dragKeys.includes(this.rowKey(targetRow))) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        let placement = 'before';
        if (targetRow) {
            const rect = e.currentTarget.getBoundingClientRect();
            placement = e.clientY > rect.top + rect.height / 2 ? 'after' : 'before';
        }
        const key = targetRow ? this.rowKey(targetRow) : null;
        if (
            this._drop?.key !== key ||
            this._drop?.placement !== placement ||
            this._drop?.groupKey !== group.key
        ) {
            this._drop = { key, placement, groupKey: group.key };
        }
    }

    _onDrop(e) {
        const moving = this._draggedRows();
        const keys = this._dragKeys;
        const drop = this._drop;
        this._dragKeys = [];
        this._drop = null;
        if (!moving.length || !drop) return;
        e.preventDefault();
        if (keys.includes(drop.key)) return;
        const group = this._groups.find((g) => g.key === drop.groupKey);
        if (!group) return;
        const { prev, next } = neighboursAfterMove(
            group.rows,
            this.rowKey,
            keys,
            drop.key,
            drop.placement,
        );
        this._emitMove(moving, prev, next, group);
    }

    /**
     * Move a row, with the rows moving with it, one place up (-1) or down (1)
     * in its group: past the nearest row that isn't moving. Answers whether
     * anything moved.
     */
    _moveByKeyboard(row, step) {
        if (!this._canReorder || !this._grabbable(row)) return false;
        const moving = this._movingRows(row);
        const keys = moving.map((r) => this.rowKey(r));
        const group = this._groupOf(row);
        if (!this._canLandIn(moving, group)) return false;
        const indexes = group.rows
            .map((r, i) => (keys.includes(this.rowKey(r)) ? i : -1))
            .filter((i) => i !== -1);
        const isStill = (r) => !keys.includes(this.rowKey(r));
        const target =
            step < 0
                ? group.rows.slice(0, indexes[0]).findLast(isStill)
                : group.rows.slice(indexes[indexes.length - 1] + 1).find(isStill);
        if (!target) return false;
        const { prev, next } = neighboursAfterMove(
            group.rows,
            this.rowKey,
            keys,
            this.rowKey(target),
            step < 0 ? 'before' : 'after',
        );
        this._emitMove(moving, prev, next, group);
        return true;
    }

    _emitMove(rows, prev, next, group) {
        this.dispatchEvent(
            new CustomEvent('row-move', {
                detail: {
                    row: rows[0],
                    rows,
                    prev,
                    next,
                    groupKey: this.state.group ? group.key : null,
                },
            }),
        );
    }

    updated(changed) {
        super.updated?.(changed);
        // Keep the highlighted choice in view, scrolling only the list itself.
        const list = /** @type {HTMLElement} */ (this.shadowRoot.querySelector('.options'));
        const lit = /** @type {HTMLElement} */ (list?.querySelector('.highlight'));
        if (list && lit) {
            if (lit.offsetTop < list.scrollTop) list.scrollTop = lit.offsetTop;
            else if (lit.offsetTop + lit.offsetHeight > list.scrollTop + list.clientHeight) {
                list.scrollTop = lit.offsetTop + lit.offsetHeight - list.clientHeight;
            }
        }
        // After a keyboard move, keep focus on the row that moved, wherever it went.
        if (this._refocus && changed.has('rows')) {
            const { key, col } = this._refocus;
            this._refocus = null;
            const rowIndex = this._visibleRows().findIndex((r) => this.rowKey(r) === key);
            if (rowIndex !== -1) this._focusCell({ row: rowIndex, col });
        }
    }

    // --- Selection ---

    _toggleSelect(key, shift) {
        const next = new Set(this.selected);
        if (shift && this._anchorKey) {
            const keys = this._visibleRows().map((r) => this.rowKey(r));
            for (const k of rangeOfIds(keys, this._anchorKey, key)) next.add(k);
        } else if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }
        this._anchorKey = key;
        this._setSelected(next);
        return next;
    }

    _toggleSelectAll(rows) {
        const keys = rows.map((r) => this.rowKey(r));
        const all = keys.length > 0 && keys.every((k) => this.selected.has(k));
        this._setSelected(all ? new Set() : new Set(keys));
    }

    _toggleGroup(key) {
        const next = new Set(this._collapsed);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        this._collapsed = next;
    }

    // --- Rendering ---

    render() {
        const groups = this._groups;
        const rows = this._visibleRows(groups);
        const span =
            this.columns.length +
            (this.reorderable ? 1 : 0) +
            (this.selectable ? 1 : 0) +
            (this.rowActions ? 1 : 0);
        return html`
            ${this._renderChips()}
            <div class="grid-wrap">
                <table>
                    <thead>
                        <tr>
                            ${this.reorderable ? html`<th class="drag"></th>` : nothing}
                            ${
                                this.selectable
                                    ? html`<th class="check">
                                <input type="checkbox" class="form-check-input" aria-label="Select all shown"
                                    .checked="${rows.length > 0 && rows.every((r) => this.selected.has(this.rowKey(r)))}"
                                    @change="${() => this._toggleSelectAll(rows)}">
                            </th>`
                                    : nothing
                            }
                            ${this.rowActions ? html`<th class="plain actions">${this.actionsLabel}</th>` : nothing}
                            ${this.columns.map((col) => this._renderHeader(col))}
                        </tr>
                    </thead>
                    <tbody>
                        ${
                            this.rows.length === 0
                                ? html`<tr><td colspan="${span}" class="empty">${this.emptyMessage}</td></tr>`
                                : this._renderGroups(groups, rows.length, span)
                        }
                    </tbody>
                </table>
            </div>
            <div class="hint">
                Press <button type="button" aria-label="Show help and keyboard shortcuts" @click="${this._openHelp}"><kbd>?</kbd></button>
                for help and keyboard shortcuts
            </div>
            ${this._renderOpenMenu()}
            ${this._renderChoices()}
            ${this._renderHelp()}
        `;
    }

    // --- Help ---

    _openHelp() {
        const active = this.shadowRoot.activeElement;
        this._helpReturn = active instanceof HTMLElement ? active : null;
        this._help = true;
        this.updateComplete.then(() =>
            /** @type {HTMLElement} */ (this.shadowRoot.querySelector('.help'))?.focus(),
        );
    }

    _closeHelp() {
        const back = this._helpReturn;
        this._help = false;
        this._helpReturn = null;
        this.updateComplete.then(() => {
            if (back?.isConnected) back.focus();
        });
    }

    _onHelpKeydown(e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            this._closeHelp();
        } else if (e.key === 'Tab') {
            // The close button is the only thing to move to; keep focus in here.
            e.preventDefault();
            /** @type {HTMLElement} */ (this.shadowRoot.querySelector('.help .btn-close'))?.focus();
        }
    }

    _renderHelp() {
        if (!this._help) return nothing;
        const groups = gridShortcuts({
            selectable: this.selectable,
            reorderable: this.reorderable,
        });
        return html`
            <div class="help-backdrop" @click="${this._closeHelp}"></div>
            <div class="help" role="dialog" aria-modal="true" aria-labelledby="grid-help-title" tabindex="-1"
                @keydown="${this._onHelpKeydown}">
                <div class="help-header">
                    <h2 id="grid-help-title">Help and keyboard shortcuts</h2>
                    <button type="button" class="btn-close btn-close-white" aria-label="Close" @click="${this._closeHelp}"></button>
                </div>
                <div class="help-body">
                    ${groups.map(
                        (g) => html`<section>
                            <h3>${g.title}</h3>
                            <dl>
                                ${g.items.map((item) => html`<dt>${this._renderKeys(item.keys)}</dt><dd>${item.does}</dd>`)}
                            </dl>
                            ${g.note ? html`<p class="note">${g.note}</p>` : nothing}
                        </section>`,
                    )}
                </div>
            </div>
        `;
    }

    _renderKeys(keys) {
        return keys.map(
            (combo, i) =>
                html`${i ? html`<span class="or">or</span>` : nothing}${combo
                    .split('+')
                    .map((key, j) => html`${j ? '+' : ''}<kbd>${key}</kbd>`)}`,
        );
    }

    // --- A select cell's choices ---

    _renderChoices() {
        const editing = this._editing;
        if (!editing?.at || editing.done) return nothing;
        const col = this._column(editing.col);
        const row = this.rows.find((r) => this.rowKey(r) === editing.key);
        if (!col || !row) return nothing;
        const current = String(col.value(row) ?? '');
        return html`
            <div class="menu options" role="listbox" tabindex="-1" aria-label="${col.label}"
                aria-activedescendant="grid-choice-${editing.highlight}"
                style="${this._menuStyle(editing.at)}; min-width: ${editing.minWidth}px"
                @mousedown="${(e) => e.preventDefault()}"
                @click="${(e) => e.stopPropagation()}"
                @keydown="${(e) => this._onChoicesKeydown(e, row, col)}"
                @focusout="${() => this._cancelEdit(false)}">
                ${this._choicesOf(col).map(
                    (
                        o,
                        i,
                    ) => html`<div id="grid-choice-${i}" role="option" aria-selected="${o.value === current}"
                        class="option ${i === editing.highlight ? 'highlight' : ''} ${o.value === current ? 'current' : ''}"
                        @mousemove="${() => i !== this._editing.highlight && ((this._editing = { ...this._editing, highlight: i }))}"
                        @click="${() => this._choose(row, col, o.value)}"><i class="bi bi-check2 tick" aria-hidden="true"></i>${o.label}</div>`,
                )}
            </div>
        `;
    }

    _renderChips() {
        const chips = [];
        const sortCol = this.state.sort && this._column(this.state.sort.key);
        if (sortCol) {
            chips.push(html`<span class="chip"><i class="bi bi-sort-${this.state.sort.dir === 'asc' ? 'down-alt' : 'down'}"></i>
                Sorted by ${sortCol.label} ${this.state.sort.dir === 'asc' ? '↑' : '↓'}
                <button aria-label="Clear sort" @click="${() => this._setState({ sort: null })}"><i class="bi bi-x"></i></button></span>`);
        }
        const groupCol = this.state.group && this._column(this.state.group);
        if (groupCol) {
            chips.push(html`<span class="chip"><i class="bi bi-collection"></i> Grouped by ${groupCol.label}
                <button aria-label="Stop grouping" @click="${() => this._setState({ group: '' })}"><i class="bi bi-x"></i></button></span>`);
        }
        for (const [key, values] of Object.entries(this.state.filters)) {
            const col = this._column(key);
            if (!col || !values.length) continue;
            const labels = distinctBuckets(this.rows, col)
                .filter((b) => values.includes(b.key))
                .map((b) => b.label);
            chips.push(html`<span class="chip"><i class="bi bi-funnel"></i> ${col.label}: ${labels.join(', ') || values.join(', ')}
                <button aria-label="Clear ${col.label} filter" @click="${() => this._clearFilter(col)}"><i class="bi bi-x"></i></button></span>`);
        }
        return chips.length ? html`<div class="chips">${chips}</div>` : nothing;
    }

    _renderHeader(col) {
        const sortable = isSortable(col);
        const groupable = isGroupable(col);
        const filterable = isFilterable(col);
        const style = col.width ? `width: ${col.width}` : '';
        if (!sortable && !groupable && !filterable) {
            return html`<th class="plain ${col.elide ? 'elide' : ''}" style="${style}">${col.label}</th>`;
        }
        const sorted = this.state.sort?.key === col.key ? this.state.sort.dir : null;
        const grouped = this.state.group === col.key;
        const filtered = Boolean(this.state.filters[col.key]?.length);
        const open = this._menu === col.key;
        return html`
            <th class="menu-host ${col.elide ? 'elide' : ''}" style="${style}">
                <button class="th-button ${open ? 'open' : ''}" aria-haspopup="menu" aria-expanded="${open}"
                    @click="${(e) => this._toggleMenu(e, col.key)}">
                    ${col.label}
                    <span class="indicators">
                        ${sorted ? html`<i class="bi bi-arrow-${sorted === 'asc' ? 'up' : 'down'}" title="Sorted"></i>` : nothing}
                        ${grouped ? html`<i class="bi bi-collection" title="Grouped"></i>` : nothing}
                        ${filtered ? html`<i class="bi bi-funnel-fill" title="Filtered"></i>` : nothing}
                    </span>
                    <i class="bi bi-chevron-down caret"></i>
                </button>
            </th>
        `;
    }

    /**
     * The open header menu. It's drawn out here, not in its header, because
     * each sticky header paints over the one before it, menu and all.
     */
    _renderOpenMenu() {
        const col = this._menu && this._column(this._menu);
        if (!col) return nothing;
        return this._renderMenu(col, {
            sortable: isSortable(col),
            groupable: isGroupable(col),
            filterable: isFilterable(col),
            sorted: this.state.sort?.key === col.key ? this.state.sort.dir : null,
            grouped: this.state.group === col.key,
        });
    }

    _menuStyle(at = this._menuAt) {
        const { top, bottom, left, maxHeight } = at;
        const edge = top == null ? `bottom: ${bottom}px` : `top: ${top}px`;
        return `${edge}; left: ${left}px; max-height: ${maxHeight}px`;
    }

    _renderMenu(col, { sortable, groupable, filterable, sorted, grouped }) {
        const values = filterable ? distinctBuckets(this.rows, col) : [];
        const chosen = new Set(this.state.filters[col.key] || []);
        return html`
            <div class="menu" role="menu"
                style="${this._menuStyle()}"
                @click="${(e) => e.stopPropagation()}"
                @keydown="${(e) => e.key === 'Escape' && ((this._menu = null))}">
                ${
                    sortable
                        ? html`
                    <button class="item ${sorted === 'asc' ? 'active' : ''}" role="menuitem" @click="${() => this._sortBy(col, 'asc')}">
                        <i class="bi bi-sort-down-alt"></i> Sort ascending</button>
                    <button class="item ${sorted === 'desc' ? 'active' : ''}" role="menuitem" @click="${() => this._sortBy(col, 'desc')}">
                        <i class="bi bi-sort-down"></i> Sort descending</button>
                    ${sorted ? html`<button class="item" role="menuitem" @click="${() => this._sortBy(col, null)}"><i class="bi bi-x"></i> Clear sort</button>` : nothing}
                `
                        : nothing
                }
                ${
                    groupable
                        ? html`
                    ${sortable ? html`<hr>` : nothing}
                    <button class="item ${grouped ? 'active' : ''}" role="menuitem" @click="${() => this._groupBy(col)}">
                        <i class="bi bi-collection"></i> ${grouped ? 'Stop grouping' : `Group by ${col.label}`}</button>
                `
                        : nothing
                }
                ${
                    filterable && values.length
                        ? html`
                    <hr>
                    <div class="section d-flex justify-content-between">
                        <span>Show only</span>
                        ${
                            chosen.size
                                ? html`<a href="#" @click="${(e) => {
                                      e.preventDefault();
                                      this._clearFilter(col);
                                  }}">Clear</a>`
                                : nothing
                        }
                    </div>
                    <div class="values">
                        ${values.map(
                            (b) => html`<label class="value">
                                <input type="checkbox" class="form-check-input mt-0" .checked="${chosen.has(b.key)}"
                                    @change="${() => this._toggleFilter(col, b.key)}">
                                ${b.label}<span class="count">${b.count}</span>
                            </label>`,
                        )}
                    </div>
                `
                        : nothing
                }
            </div>
        `;
    }

    _renderGroups(groups, rowCount, span) {
        let rowIndex = 0;
        const groupCol = this.state.group && this._column(this.state.group);
        return groups.map((g) => {
            const collapsed = this._collapsed.has(g.key);
            const header = groupCol
                ? html`<tr class="group-row ${this._drop?.groupKey === g.key && this._drop.key == null ? 'drop-into' : ''}"
                    @click="${() => this._toggleGroup(g.key)}"
                    @dragover="${(e) => this._onDragOver(e, g, null)}"
                    @drop="${(e) => this._onDrop(e)}">
                    <td colspan="${span}">
                        <i class="bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-down'}"></i>
                        ${g.label}<span class="group-count">${g.rows.length}</span>
                        ${this.groupExtra ? this.groupExtra(g, groupCol) : nothing}
                    </td>
                </tr>`
                : nothing;
            const body = collapsed
                ? []
                : g.rows.map((row) => this._renderRow(row, rowIndex++, rowCount, g));
            return html`${header}${body}`;
        });
    }

    _renderRow(row, rowIndex, rowCount, group) {
        const key = this.rowKey(row);
        const selected = this.selected.has(key);
        const drop = this._drop?.key === key ? `drop-${this._drop.placement}` : '';
        const dragging = this._dragKeys.includes(key) ? 'dragging' : '';
        return html`
            <tr class="grid-row ${selected ? 'selected' : ''} ${drop} ${dragging}"
                @dragover="${(e) => this._onDragOver(e, group, row)}"
                @drop="${(e) => this._onDrop(e)}">
                ${this.reorderable ? this._renderGrip(row) : nothing}
                ${
                    this.selectable
                        ? html`<td class="check">
                    <input type="checkbox" class="form-check-input" .checked="${selected}" aria-label="Select row"
                        @click="${(e) => {
                            // Not preventDefault: the browser would untick the box after the
                            // render ticked it. Set it to what the selection says instead.
                            e.target.checked = this._toggleSelect(key, e.shiftKey).has(key);
                        }}">
                </td>`
                        : nothing
                }
                ${this.rowActions ? html`<td class="actions">${this.rowActions(row)}</td>` : nothing}
                ${this.columns.map((col, colIndex) => this._renderCell(row, col, rowIndex, colIndex, rowCount))}
            </tr>
        `;
    }

    _renderCell(row, col, rowIndex, colIndex, rowCount) {
        const editing = this._editing?.key === this.rowKey(row) && this._editing.col === col.key;
        const focused = this._focus
            ? this._focus.row === rowIndex && this._focus.col === colIndex
            : rowIndex === 0 && colIndex === 0;
        const editable = this._editable(col, row);
        const readonly = !editable;
        const refused = this._refused?.key === this.rowKey(row) && this._refused.col === col.key;
        return html`
            <td class="${col.elide ? 'elide' : ''}">
                <div class="cell ${col.className || ''} ${readonly ? 'readonly' : ''} ${editable ? 'editable' : ''} ${refused ? 'refused' : ''} ${editing ? 'editing' : ''}"
                    data-row="${rowIndex}" data-col="${colIndex}" tabindex="${focused ? 0 : -1}"
                    title="${col.elide ? String(col.value(row) ?? '') : readonly ? this._readonlyTitle(col, row) : ''}"
                    @focus="${() => (this._focus = { row: rowIndex, col: colIndex })}"
                    @dblclick="${() => this._startEdit(row, col)}"
                    @keydown="${(e) => this._onCellKeydown(e, row, rowIndex, colIndex, rowCount)}">
                    ${editing && col.editor === 'text' ? this._renderEditor(row, col) : html`<span class="value">${this._renderValue(row, col)}</span>`}
                    ${readonly ? html`<i class="bi bi-lock-fill lock" aria-hidden="true"></i>` : nothing}
                </div>
            </td>
        `;
    }

    _readonlyTitle(col, row) {
        const title = col.readonlyTitle;
        return (typeof title === 'function' ? title(row) : title) || '';
    }

    _renderValue(row, col) {
        if (col.display) return col.display(row);
        if (col.options || col.bucket) {
            const b = bucketOf(col, row);
            return b.key === '' ? html`<span class="text-muted">${b.label}</span>` : b.label;
        }
        const value = col.value(row);
        return value == null || value === ''
            ? html`<span class="text-muted">${col.emptyLabel || '—'}</span>`
            : value;
    }

    /** A text cell's editor; a select cell edits from its list of choices (_renderChoices). */
    _renderEditor(row, col) {
        const onKeydown = (e) => this._onEditorKeydown(e, row, col);
        const onBlur = () => this._onEditorBlur();
        const value = this._editing.seed ?? String(col.value(row) ?? '');
        return html`<input class="form-control form-control-sm editor" .value="${value}" @keydown="${onKeydown}" @blur="${onBlur}">`;
    }
}

customElements.define('smart-grid', SmartGrid);
